import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Network, Users, Handshake, Phone, MessageCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitLead } from "@/lib/api";
import { CONTACT } from "@/data/content";
import { trackEvent } from "@/lib/analytics";
import { cn } from "@/lib/utils";

const PATHS = [
    {
        n: "01", icon: Users, type: "Direct Partner", cta: "ENROLL AS DIRECT PARTNER",
        tag: "For professionals, entrepreneurs and local networkers",
        points: ["Customer & lead generation", "Customer meetings", "Follow-up coordination", "Site visit coordination", "Local networking", "Project opportunities", "OMSIVO ecosystem access"],
    },
    {
        n: "02", icon: Network, type: "City Franchise Partner", cta: "APPLY FOR CITY FRANCHISE",
        tag: "City / territory business development",
        points: ["Local market development", "Customer acquisition", "Site visits", "Local networking", "Marketing", "Lead conversion", "Sub-branch development", "Project opportunities"],
    },
    {
        n: "03", icon: Handshake, type: "Sub-Partner", cta: "ENROLL AS SUB-PARTNER",
        tag: "Local referrals and coordination",
        points: ["Local lead generation", "Customer referrals", "Networking", "Customer meetings", "Site coordination", "Follow-up", "Project opportunities"],
    },
];

const fieldCls = "h-12 w-full rounded-xl border border-white/15 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-flame";

const PartnerForm = () => {
    const [params] = useSearchParams();
    const [form, setForm] = useState({ partnership_type: params.get("type") || "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const set = (id) => (e) => setForm((f) => ({ ...f, [id]: e.target.value }));
    const isFranchise = form.partnership_type === "City Franchise Partner";

    useEffect(() => {
        const onType = (e) => setForm((f) => ({ ...f, partnership_type: e.detail }));
        window.addEventListener("omsivo:partner-type", onType);
        return () => window.removeEventListener("omsivo:partner-type", onType);
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || form.name.trim().length < 2) return toast.error("Please enter your full name.");
        if (!form.mobile || form.mobile.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid mobile number.");
        if (!form.partnership_type) return toast.error("Please select a partnership type.");
        setBusy(true);
        try {
            const res = await submitLead({
                form_type: "partner",
                name: form.name,
                mobile: form.mobile,
                email: form.email || undefined,
                location: [form.city, form.state].filter(Boolean).join(", ") || undefined,
                requirement: form.partnership_type,
                message: form.message || undefined,
                source_page: "/partner",
                extra: {
                    partnership_type: form.partnership_type,
                    profession: form.profession,
                    business_experience: form.experience,
                    local_network: form.network,
                    areas_of_interest: form.interests,
                    preferred_territory: form.territory,
                    existing_office: form.existing_office,
                    existing_business: form.existing_business,
                    construction_experience: form.construction_experience,
                },
            });
            setDone(res);
            trackEvent("partner_application_submitted", { type: form.partnership_type, lead_id: res.lead_id });
            toast.success("Application received", { description: `Lead ID: ${res.lead_id}` });
        } catch {
            toast.error("Something went wrong. Please try WhatsApp or call 73-52-52-52-37.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="flex flex-col items-center px-6 py-16 text-center" data-testid="partner-success">
                <CheckCircle2 className="h-14 w-14 text-flame" />
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">Application received.</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-white/65">{done.message}</p>
                <p className="mt-5 rounded-full border border-flame/40 bg-flame/10 px-5 py-2 font-mono text-xs tracking-[0.18em] text-flame" data-testid="partner-lead-id">
                    LEAD ID · {done.lead_id}
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="partner-form">
            {[
                { id: "name", label: "Full Name", required: true, placeholder: "Your full name" },
                { id: "mobile", label: "Mobile Number", required: true, placeholder: "+91", type: "tel" },
                { id: "email", label: "Email", placeholder: "you@email.com", type: "email" },
                { id: "city", label: "City", placeholder: "Patna" },
                { id: "state", label: "State", placeholder: "Bihar" },
                { id: "profession", label: "Current Profession / Business", placeholder: "e.g. Real estate agent" },
                { id: "experience", label: "Business Experience", placeholder: "e.g. 5 years in sales" },
                { id: "network", label: "Existing Local Network", placeholder: "e.g. Local business community" },
                { id: "interests", label: "Areas of Interest", placeholder: "e.g. Residential projects" },
            ].map((f) => (
                <div key={f.id}>
                    <label htmlFor={`partner-${f.id}`} className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                        {f.label}{f.required && <span className="text-flame"> *</span>}
                    </label>
                    <Input id={`partner-${f.id}`} type={f.type || "text"} value={form[f.id] || ""} onChange={set(f.id)} placeholder={f.placeholder}
                        data-testid={`partner-field-${f.id}`} className={fieldCls} />
                </div>
            ))}
            <div>
                <label htmlFor="partner-type" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                    Partnership Type<span className="text-flame"> *</span>
                </label>
                <select id="partner-type" value={form.partnership_type || ""} onChange={set("partnership_type")}
                    data-testid="partner-field-partnership_type"
                    className={cn(fieldCls, "appearance-none", !form.partnership_type && "text-white/35")}>
                    <option value="" className="bg-omsivo-deep">{`Select partnership type`}</option>
                    {PATHS.map((p) => <option key={p.type} value={p.type} className="bg-omsivo-deep">{p.type}</option>)}
                </select>
            </div>
            {isFranchise && (
                <>
                    {[
                        { id: "territory", label: "Preferred City / Territory", placeholder: "e.g. Darbhanga district" },
                        { id: "existing_business", label: "Existing Business", placeholder: "e.g. Building material shop" },
                    ].map((f) => (
                        <div key={f.id}>
                            <label htmlFor={`partner-${f.id}`} className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{f.label}</label>
                            <Input id={`partner-${f.id}`} value={form[f.id] || ""} onChange={set(f.id)} placeholder={f.placeholder}
                                data-testid={`partner-field-${f.id}`} className={fieldCls} />
                        </div>
                    ))}
                    {[
                        { id: "existing_office", label: "Existing Office" },
                        { id: "construction_experience", label: "Construction / Real Estate Experience" },
                    ].map((f) => (
                        <div key={f.id}>
                            <label htmlFor={`partner-${f.id}`} className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{f.label}</label>
                            <select id={`partner-${f.id}`} value={form[f.id] || ""} onChange={set(f.id)}
                                data-testid={`partner-field-${f.id}`}
                                className={cn(fieldCls, "appearance-none", !form[f.id] && "text-white/35")}>
                                <option value="" className="bg-omsivo-deep">{`Select`}</option>
                                <option value="Yes" className="bg-omsivo-deep">Yes</option>
                                <option value="No" className="bg-omsivo-deep">No</option>
                            </select>
                        </div>
                    ))}
                </>
            )}
            <div className="sm:col-span-2">
                <label htmlFor="partner-message" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">Message</label>
                <Textarea id="partner-message" value={form.message || ""} onChange={set("message")} rows={4}
                    placeholder="Tell us about yourself and your local network..."
                    data-testid="partner-field-message"
                    className="rounded-xl border-white/15 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-flame" />
            </div>
            <div className="sm:col-span-2">
                <button type="submit" disabled={busy} data-testid="partner-submit-btn"
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-flame py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-all hover:-translate-y-0.5 hover:bg-flame-dark disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    {busy ? "Submitting..." : "Start Partnership Discussion"}
                </button>
                <p className="mt-3 text-center font-mono text-[9px] uppercase leading-relaxed tracking-[0.16em] text-white/35">
                    Detailed commercial terms are discussed during onboarding.<br />Nothing on this page is a guarantee of income, leads or projects.
                </p>
            </div>
        </form>
    );
};

export default function Partner() {
    const scrollToForm = (type) => {
        window.dispatchEvent(new CustomEvent("omsivo:partner-type", { detail: type }));
        const el = document.getElementById("partner-form-section");
        if (el) {
            if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.2 });
            else el.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <div className="bg-omsivo-paper">
            <Navbar />
            <section className="blueprint-fine noise relative overflow-hidden pb-20 pt-28 lg:pt-36" data-testid="partner-hero">
                <div className="relative z-10 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <Reveal y={16}>
                        <p className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.3em] text-white/70 backdrop-blur">
                            <span className="h-1.5 w-1.5 rounded-full bg-flame" /> Partner with us
                        </p>
                    </Reveal>
                    <MaskedLines
                        className="mt-8 font-display font-black uppercase leading-[1.0] tracking-tight"
                        lineClassName="text-4xl text-white sm:text-6xl lg:text-7xl"
                        lines={[<>PARTNER WITH <span className="text-flame">OMSIVO.</span></>]}
                    />
                    <Reveal delay={0.4}>
                        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/75 sm:text-xl">
                            BUILD YOUR BUSINESS. BUILD YOUR NETWORK. BUILD WITH OMSIVO.
                        </p>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/55">
                            A performance-based partnership ecosystem across local network development, customer relationships and project opportunities — backed by OMSIVO central support.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-20 lg:py-28" data-testid="partner-paths">
                <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                        {PATHS.map((p, i) => (
                            <Reveal key={p.type} delay={0.07 * i} className="h-full">
                                <div className="lift-card flex h-full flex-col rounded-[24px] border border-omsivo-navy/10 bg-white p-8" data-testid={`partner-path-${i}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-omsivo-navy/5 text-omsivo-blue">
                                            <p.icon className="h-6 w-6" />
                                        </div>
                                        <span className="font-display text-4xl font-black text-omsivo-navy/10">{p.n}</span>
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-extrabold uppercase tracking-[0.05em] text-omsivo-navy">{p.type}</h3>
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-flame-dark">{p.tag}</p>
                                    <ul className="mt-6 space-y-2.5 border-t border-omsivo-navy/8 pt-6">
                                        {p.points.map((pt) => (
                                            <li key={pt} className="flex items-center gap-2.5 text-sm text-slate-700">
                                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />{pt}
                                            </li>
                                        ))}
                                    </ul>
                                    <button onClick={() => scrollToForm(p.type)} data-testid={`partner-cta-${i}`}
                                        className="mt-6 w-full rounded-full bg-omsivo-navy py-3.5 font-display text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-flame-dark">
                                        {p.cta}
                                    </button>
                                    <div className="flex-1" />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <p className="mt-8 max-w-3xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.16em] text-slate-500">
                            Performance-based partnership · Business opportunities · Local network development · OMSIVO ecosystem · Central support.
                            Commercial terms are shared during onboarding. Details not yet published: to be confirmed by OMSIVO.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section id="partner-form-section" className="blueprint noise relative overflow-hidden py-20 lg:py-28" data-testid="partner-form-section">
                <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
                    <Reveal>
                        <div className="rounded-[28px] border border-white/10 bg-omsivo-royal/80 p-6 shadow-2xl shadow-black/40 backdrop-blur-xl sm:p-10">
                            <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-flame">Partner Application</p>
                            <h2 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-wide text-white sm:text-3xl">
                                Start the conversation.
                            </h2>
                            <div className="mt-8">
                                <PartnerForm />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-omsivo-void py-14" data-testid="partner-contact-strip">
                <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-center gap-4 px-4 sm:px-6">
                    <a href={CONTACT.phoneTel} data-testid="partner-phone"
                        className="flex items-center gap-2.5 rounded-full border border-white/15 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-flame">
                        <Phone className="h-4 w-4 text-flame" /> {CONTACT.phoneDisplay}
                    </a>
                    <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="partner-whatsapp"
                        className="flex items-center gap-2.5 rounded-full border border-white/15 px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-flame">
                        <MessageCircle className="h-4 w-4 text-flame" /> Talk to OMSIVO
                    </a>
                </div>
            </section>
            <Footer />
        </div>
    );
}
